import React, { useEffect, useMemo, useState } from "react";
import { Tabs, ConfigProvider, Skeleton } from "antd";
import CreditCardEmi from "./creditCardEmi";
import DebitCardEmi from "./debitCardEmi";
import OtherEmi from "./otherEmi";
import "./emi.css";
import { useDispatch, useSelector } from "react-redux";
import { removeSelectedIntruments, selectSelectedIntrument } from "../../../../slices/SavedInstrumentSlice";
import {
  changeBankDropdown,
  changeDebitBankDropdown,
  handleEmiPayButton,
  selectedTab,
  setEmiPaymentDetails,
} from "../../../../slices/EmiSlice";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../Utils/axiosInstance";
import { generatePaymentOptionsDictionary, getNetAmount } from "../../../../Utils/utils";
import { selectActivePaymentMethod } from "../../../../slices/PaymentButtonSlice";
import { emiCategorySelectedEvent } from "../../../../Utils/uiAnalytics";

const Emi = ({ emiMethodInfo }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const paymentMethodDictionary = useSelector(state => state.paymentSessionDetails?.paymentMethodDictionary);
  const emiPaymentDetails = useSelector(state => state.emiCardDetails.emiPaymentDetails)
  const activePaymentMethod = useSelector((state) => selectActivePaymentMethod(state));
  const surchargeDetails = useSelector(state => state.surcharges.surchargeDetail);
  const surchargeLoader = useSelector(state => state.surcharges.surchargeLoader);
  const appliedOfferDetail = useSelector(state => state.offers.appliedOfferDetail);
  const selectedInstrument = useSelector((state) => selectSelectedIntrument(state));
  const dccQuotation = useSelector(state => state.cardDetails.dccQuotation);
  const dccMoney = dccQuotation?.dccQuotationDetails?.dccMoney?.amount;
  const paymentDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails.paymentDetails);
  const showTotalPaymentSummaryField = useSelector((state) => state.additionalComponents.showTotalPaymentSummaryField);
  const totalPaymentSummaryValue = useSelector((state) => state.additionalComponents.totalPaymentSummaryValue);

  const memorizedOfferDetails = useMemo(() => appliedOfferDetail, [appliedOfferDetail])
  const memorizedSurchargeDetails = useMemo(() => surchargeDetails, [surchargeDetails])
  const memorizedSurchargeLoader = useMemo(() => surchargeLoader, [surchargeLoader])

  const getAmount = (surchargeTotalFee) => getNetAmount({
    activePaymentMethod,
    selectedInstrument, dccMoney, amount: paymentDetails?.money?.amount,
    showTotalPaymentSummaryField, totalPaymentSummaryValue, offerAmount: appliedOfferDetail?.finalAmount,
    surchargeAmount: surchargeTotalFee
  });
  const [loader, setLoader] = useState(false)

  const [tabs, setTabs] = useState([
    {
      key: "1",
      id: "CreditCardEMI",
      label: t("CREDIT_CARD_EMI"),
      children: <CreditCardEmi />,
    },
    {
      key: "2",
      id: "DebitCardEMI",
      label: t("DEBIT_CARD_EMI"),
      children: <DebitCardEmi />,
    },
    {
      key: "3",
      id: "CardlessEMI",
      label: t("OTHER_EMI_OPTIONS"),
      children: <OtherEmi emiMethodInfo={emiMethodInfo} />,
    },
  ]);
  const [selectedTabId, setSelectedTabId] = useState("1"); // Initial selected tab ID
  const headerColor = sessionDetails?.merchantDetails?.checkoutTheme?.headerColor

  const getTabsDetail = (paymentData) => {
    const updatedTabs = tabs.map((tab) => {
      const paymentMethod = paymentData.Emi?.find(
        (method) => method?.brand === tab?.id
      );
      // const paymentMethod = sessionDetails?.configs?.paymentMethods?.find(
      //   (method) => method?.brand === tab?.id
      // );
      const showContent = !!paymentMethod;
      const label = showContent ? paymentMethod?.title : tab.label;

      let obj = { ...tab, showContent, label: t(`${label.toUpperCase().replaceAll(' ', '_')}`) }
      return obj;
    });
    setTabs(updatedTabs);

    const visibleTabs = updatedTabs?.filter((tab) => tab?.showContent);
    if (visibleTabs?.length === 1) {
      setSelectedTabId(visibleTabs[0].key);
    }
  }

  const fetchTempPaymentMethods = async (surchargeTotalFee) => {
    try {
      setLoader(true)
      const baseUrl = process.env.REACT_APP_BASE_URL;
      const amount = getAmount(surchargeTotalFee)
      const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let url = `${baseUrl}/checkout/sessions/${sessionDetails?.token}/payment-methods`;
      // Properly construct query parameters
      const params = new URLSearchParams();
      params.append("amount", amount);
      params.append("browserTimeZone", browserTimeZone)
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      const response = await axiosInstance.get(url);
      const dict = generatePaymentOptionsDictionary(response?.data);
      dispatch(setEmiPaymentDetails(dict))
      getTabsDetail(dict)
      setLoader(false)
    } catch (error) {
      getTabsDetail(paymentMethodDictionary)
      setLoader(false)
    }
  }

  useEffect(() => {
    if (sessionDetails && !surchargeLoader) {
      const activeSurchargeDetail = surchargeDetails?.appliedSurcharges?.filter(({ surchargeDetails }) =>
        !surchargeDetails.applicableOn || surchargeDetails?.applicableOn?.toLowerCase() == "emi"
      );
      let surchargeTotalFee = 0
      if (activeSurchargeDetail?.length) {
        activeSurchargeDetail.forEach(el => surchargeTotalFee += el.calculatedSurchargeFee)

        if (emiPaymentDetails?.length) {
          getTabsDetail(emiPaymentDetails)
        } else {
          fetchTempPaymentMethods(surchargeTotalFee)
        }
      } else {
        getTabsDetail(paymentMethodDictionary)
      }
    }
  }, [sessionDetails, i18n.language, memorizedSurchargeDetails, memorizedSurchargeLoader]);

  const visibleTabs = tabs?.filter((tab) => tab.showContent);

  const handleTabChange = (key) => {
    setSelectedTabId(key);
    dispatch(removeSelectedIntruments());
    const selectedTab = tabs.find((tab) => tab.key === key);
    emiCategorySelectedEvent(selectedTab?.label);
  };

  // Emi only mounts once the shopper navigates into the EMI category, and the
  // default tab (Credit Card) is selected without a click firing handleTabChange -
  // report that default selection as soon as it comes into view.
  useEffect(() => {
    const defaultTab = tabs.find((tab) => tab.key === selectedTabId);
    emiCategorySelectedEvent(defaultTab?.label);
  }, []);

  useEffect(() => {
    if (selectedTabId === "1") {
      dispatch(selectedTab("emi/cc"));
      dispatch(changeBankDropdown("All Banks"));
      dispatch(handleEmiPayButton(true));
    } else if (selectedTabId === "2") {
      dispatch(selectedTab("emi/dc"));
      dispatch(changeDebitBankDropdown("All Banks"));
      dispatch(handleEmiPayButton(true));
    } else {
      dispatch(handleEmiPayButton(true));
    }
  }, [selectedTabId]);

  return (
    <div className="tab-div-stylings">
      {loader ? <Skeleton active size="medium" /> : <ConfigProvider
        theme={{
          components: {
            Tabs: {
              itemColor: "inherit",
              itemHoverColor: "inherit",
              itemSelectedColor: "inherit",
              inkBarColor: "#000",
            },
          },
        }}
      >
        <Tabs
          type="line"
          items={visibleTabs}
          defaultActiveKey={selectedTabId}
          onChange={handleTabChange}
          tabBarStyle={{ color: 'black' }}
          // tabBarGutter={32}
          moreIcon={null} // ✅ hides the 3-dot icon
        />
      </ConfigProvider>}

    </div>
  );
};

export default Emi;


